import { observer } from "mobx-react";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, ClickAwayListener, Grid, IconButton, Paper, Popper, Typography } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import AccountBoxOutlinedIcon from "@material-ui/icons/AccountBoxOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import Avatar from "uiKit/labels/avatar";
import { useMenuProfileStyles, usePopupStyles } from "components/menuProfile/styles";
import MenuItems from "components/menuItems";
import { useRootStore } from "app/useRootStore";
import { useLocalize } from "shared/hooks/useLocalize";
import { LocalizationId } from "components/menuProfile/localization";
import routeNames from "shared/routes/constants/routeNames";

const MenuProfile: React.FC = (): JSX.Element => {
    const {
        rootStore: {
            authStore,
            myProfileStore: { avatar, firstName, lastName },
            layoutStore: { closeMenu },
        },
    } = useRootStore();
    const classes = useMenuProfileStyles({ palette: "default" });
    const popupStyles = usePopupStyles();
    const localize = useLocalize();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const accountTypeLabel = authStore.getAccountTypeLabel(localize);

    const openPopperMenu = () => {
        setOpen(prevOpen => !prevOpen);
        closeMenu();
    };

    const handleClose = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current && !open) {
            anchorRef.current!.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const menuItems = useMemo(
        () => [
            {
                sortOrder: 1,
                name: localize(LocalizationId.ProfileMenuItem),
                route: routeNames.MY_PROFILE,
                iconLink: "",
                iconElement: <AccountBoxOutlinedIcon />,
                children: [],
            },
            {
                sortOrder: 2,
                name: localize(LocalizationId.LogoutMenuItem),
                iconLink: "",
                iconElement: <ExitToAppIcon />,
                children: [],
                onClick: () => authStore.logout(),
            },
        ],
        [authStore, localize]
    );

    const userRoleOrAccountType = useMemo(() => {
        return accountTypeLabel;
    }, [accountTypeLabel]);

    return (
        <Grid container wrap="nowrap" className={classes.root}>
            <Grid item>
                <Avatar src={avatar} firstName={firstName} lastName={lastName} />
            </Grid>
            <Grid item container direction="column" justify="center" className={classes.profileContainer}>
                <Grid item container wrap="nowrap" alignItems="center" className={classes.subMenuIconContainer}>
                    <Typography variant="h4" className={classes.heading}>
                        {firstName}
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography className={classes.roleOrAcoountTypeTypography} variant="caption">
                        {userRoleOrAccountType}
                    </Typography>
                </Grid>
            </Grid>
            <Box className={classes.toggleMobilePopperIcon}>
                <IconButton
                    id="long-menu-button"
                    aria-controls="long-menu"
                    aria-haspopup="true"
                    onClick={openPopperMenu}
                    color="primary"
                    className={classes.subMenuIcon}
                    ref={anchorRef}
                >
                    <ArrowDropDownIcon />
                </IconButton>
                <Popper
                    id="long-menu"
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom"
                    transition
                    className={popupStyles.list}
                >
                    <Paper className={popupStyles.paper}>
                        <ClickAwayListener onClickAway={handleClose}>
                            <div>
                                <MenuItems menuItems={menuItems} isPopup handleClose={handleClose} />
                            </div>
                        </ClickAwayListener>
                    </Paper>
                </Popper>
            </Box>
        </Grid>
    );
};

export default observer(MenuProfile);
